import {
  linearToSrgb,
  srgbFrom8Bit,
  srgbToLinear,
  type LinearRGB,
  type SRGB,
} from '../color/index.js';

/**
 * The subset of `ImageData` this module needs.
 *
 * Declaring it structurally rather than importing the DOM type keeps region
 * sampling — the only part of capture with real logic in it — testable in plain
 * Node against hand-built pixel buffers.
 */
export interface ImageDataLike {
  data: Uint8ClampedArray | Uint8Array | number[];
  width: number;
  height: number;
}

export interface RegionSample {
  /** Mean colour of the region, averaged in linear light after trimming. */
  srgb: SRGB;
  linear: LinearRGB;
  /** Pixels that contributed to the mean, after trimming. */
  pixelCount: number;
  /** Pixels examined before trimming. */
  totalPixels: number;
  /**
   * Per-channel standard deviation in linear light, over the retained pixels.
   * High values mean the region straddled an edge, a specular highlight or a
   * shadow, so the mean is not describing a single surface.
   */
  stdDev: LinearRGB;
  /**
   * Fraction of examined pixels with any channel at the 8-bit rails.
   *
   * A clipped pixel carries no information about the surface — the true value
   * is somewhere beyond the rail and the sensor cannot say where. Averaging it
   * in biases the result toward the middle of the range, silently. Any nonzero
   * value here should be treated as a warning; anything above a percent or two
   * makes the sample unusable.
   */
  clippedFraction: number;
}

export interface RegionOptions {
  /** Centre of the region, in pixels. */
  x: number;
  y: number;
  /** Side length of the square region, in pixels. Clamped to at least 1. */
  size: number;
  /**
   * Fraction of pixels to discard from each end of the luminance ranking
   * before averaging, in [0, 0.45]. Defaults to 0.1.
   *
   * Trimming is by luminance rank across whole pixels rather than per channel,
   * so discarding a specular speckle removes the whole pixel and leaves the
   * colour balance of the remainder intact. Trimming each channel
   * independently would shift hue.
   */
  trim?: number;
}

export const DEFAULT_TRIM = 0.1;

/** Above this, treat a sample as contaminated rather than merely imperfect. */
export const CLIPPING_WARN_FRACTION = 0.01;

/** Rec. 709 / sRGB luminance weights, for ranking pixels in linear light. */
const LUMA_R = 0.21267285140562253;
const LUMA_G = 0.71515215528781795;
const LUMA_B = 0.072174993306559562;

/**
 * Average a square region of pixels.
 *
 * Two things here matter more than they look:
 *
 * 1. Averaging happens in linear light, not gamma-encoded sRGB. Because the
 *    transfer function is non-linear, averaging encoded values biases the
 *    result dark, and the error grows with the contrast inside the region. For
 *    a half-black, half-white region the encoded mean is 128 where the correct
 *    answer is 188.
 * 2. The mean is trimmed by luminance rank. Paint has specks, cards have dust,
 *    and satin sheen throws pinpoint highlights; a plain mean lets a handful of
 *    blown pixels drag the result. See {@link RegionOptions.trim}.
 *
 * Regions are clipped to the image bounds rather than rejected, so sampling
 * near an edge still returns whatever pixels genuinely exist.
 */
export function sampleRegion(image: ImageDataLike, options: RegionOptions): RegionSample {
  const size = Math.max(1, Math.round(options.size));
  const trim = Math.min(0.45, Math.max(0, options.trim ?? DEFAULT_TRIM));
  const half = Math.floor(size / 2);
  const centreX = Math.round(options.x);
  const centreY = Math.round(options.y);

  const minX = Math.max(0, centreX - half);
  const maxX = Math.min(image.width - 1, centreX + half);
  const minY = Math.max(0, centreY - half);
  const maxY = Math.min(image.height - 1, centreY + half);

  if (minX > maxX || minY > maxY) {
    throw new RangeError(
      `Sample region centred at (${centreX}, ${centreY}) lies outside the ${image.width}x${image.height} frame.`,
    );
  }

  const pixels: Array<{ lin: LinearRGB; luma: number }> = [];
  let clipped = 0;

  for (let y = minY; y <= maxY; y++) {
    for (let x = minX; x <= maxX; x++) {
      const offset = (y * image.width + x) * 4;
      const r8 = image.data[offset] as number;
      const g8 = image.data[offset + 1] as number;
      const b8 = image.data[offset + 2] as number;

      if (r8 === 0 || g8 === 0 || b8 === 0 || r8 === 255 || g8 === 255 || b8 === 255) {
        clipped++;
      }

      const lin = srgbToLinear(srgbFrom8Bit(r8, g8, b8));
      pixels.push({ lin, luma: LUMA_R * lin.r + LUMA_G * lin.g + LUMA_B * lin.b });
    }
  }

  const total = pixels.length;
  pixels.sort((p, q) => p.luma - q.luma);

  // Never trim away everything: a 1-pixel region must still return that pixel.
  const drop = Math.min(Math.floor(total * trim), Math.floor((total - 1) / 2));
  const kept = pixels.slice(drop, total - drop);

  let sumR = 0;
  let sumG = 0;
  let sumB = 0;
  for (const p of kept) {
    sumR += p.lin.r;
    sumG += p.lin.g;
    sumB += p.lin.b;
  }
  const n = kept.length;
  const mean: LinearRGB = { r: sumR / n, g: sumG / n, b: sumB / n };

  let sqR = 0;
  let sqG = 0;
  let sqB = 0;
  for (const p of kept) {
    sqR += (p.lin.r - mean.r) ** 2;
    sqG += (p.lin.g - mean.g) ** 2;
    sqB += (p.lin.b - mean.b) ** 2;
  }

  return {
    srgb: linearToSrgb(mean),
    linear: mean,
    pixelCount: n,
    totalPixels: total,
    stdDev: {
      r: Math.sqrt(sqR / n),
      g: Math.sqrt(sqG / n),
      b: Math.sqrt(sqB / n),
    },
    clippedFraction: clipped / total,
  };
}

/**
 * Largest standard deviation across the three channels — a single-number
 * uniformity score for deciding whether a sample is trustworthy.
 *
 * Note the scope: this describes variation *inside* one small region. It says
 * nothing about whether illumination is even across the frame, which is a
 * different and often larger problem. Use {@link assessIllumination} for that.
 */
export function uniformityScore(sample: RegionSample): number {
  return Math.max(sample.stdDev.r, sample.stdDev.g, sample.stdDev.b);
}

/** True when enough of the region hit the rails to distrust the mean. */
export function isClipped(sample: RegionSample, threshold = CLIPPING_WARN_FRACTION): boolean {
  return sample.clippedFraction > threshold;
}
